// src/features/delegations/pages/DelegationDetailPage/tabs/index.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  InfoOutlined as InfoIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import InfoTab from './InfoTab';
import RevisionsTab from './RevisionsTab';

function TabPanel({ children, value, index, sx }) {
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`delegation-tabpanel-${index}`}
      aria-labelledby={`delegation-tab-${index}`}
      sx={sx}
    >
      {value === index && children}
    </Box>
  );
}

export default function DelegationTabs({ delegation }) {
  const { t } = useTranslation(['delegations']);
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const TABS = [
    {
      key: 'info',
      label: t('delegations:tabs.info.label'),
      description: t('delegations:tabs.info.description'),
      icon: InfoIcon,
    },
    {
      key: 'revisions',
      label: t('delegations:tabs.revisions.label'),
      description: t('delegations:tabs.revisions.description'),
      icon: HistoryIcon,
    },
  ];

  const getTabIndex = () => {
    const param = searchParams.get('tab');
    const idx = TABS.findIndex((tab) => tab.key === param);
    return idx !== -1 ? idx : 0;
  };

  const [currentTab, setCurrentTab] = useState(getTabIndex);

  useEffect(() => {
    setCurrentTab(getTabIndex());
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTabChange = (_, newValue) => {
    setCurrentTab(newValue);
    const next = new URLSearchParams(searchParams);
    next.set('tab', TABS[newValue].key);
    setSearchParams(next);
  };

  return (
    <Paper
      elevation={0}
      sx={{ border: '1px solid', borderColor: 'divider', flex: 1, display: "flex", flexDirection: "column", borderRadius: 3, overflow: 'hidden' }}
    >
      <Tabs
        value={currentTab}
        onChange={handleTabChange}
        aria-label="delegation detail tabs"
        variant={isMobile ? 'fullWidth' : 'standard'}
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 500,
            fontSize: isMobile ? '0.75rem' : '0.875rem',
            minHeight: isMobile ? 56 : 64,
            py: isMobile ? 1.5 : 2,
            px: isMobile ? 1.5 : 3,
            '&.Mui-selected': { fontWeight: 600, bgcolor: 'background.paper' },
          },
          '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0' },
        }}
      >
        {TABS.map((tab, index) => {
          const Icon = tab.icon;
          return (
            <Tab
              key={tab.key}
              icon={<Icon sx={{ fontSize: isMobile ? 20 : 22 }} />}
              iconPosition="start"
              label={
                <Box sx={{ textAlign: 'left', ml: isMobile ? 0.5 : 1 }}>
                  <Typography variant="body2" fontWeight="inherit">{tab.label}</Typography>
                  {!isMobile && (
                    <Typography variant="caption" color="text.secondary">{tab.description}</Typography>
                  )}
                </Box>
              }
              sx={{ alignItems: 'center', justifyContent: 'flex-start', minWidth: 'unset' }}
              id={`delegation-tab-${index}`}
              aria-controls={`delegation-tabpanel-${index}`}
            />
          );
        })}
      </Tabs>

      <Box flex="1" display="flex" flexDirection="column" minHeight={0}>
        <TabPanel value={currentTab} index={0}>
          <InfoTab delegation={delegation} />
        </TabPanel>
        <TabPanel value={currentTab} index={1} sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <RevisionsTab delegationId={delegation.delegation_id} delegation={delegation} />
        </TabPanel>
      </Box>
    </Paper>
  );
}
